import { useEffect, useRef, useState } from 'react'

import { getRequestProfileTabCounters } from '../../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { useAsyncCallback } from '../../../../../hooks'

import { getRequestProfileTabCountersParams } from './useRequestProfileTabsCounters.utils'

function useRequestProfileTabsCounters({ outboundUuid, requestId }) {
  const [loaded, setLoaded] = useState(EMPTY_OBJECT)

  const lastCallIdRef = useRef(0)
  const landedCallIdRef = useRef(0)

  // Only the latest call may land. A sections reload merges into landed
  // counters, so while another call is in flight (or before the first full
  // load) it is promoted to a full one instead of racing it.
  const handleLoadCounters = useAsyncCallback(
    (sections = EMPTY_LIST) => {
      const isPartial =
        sections.length > 0 &&
        loaded.requestId === requestId &&
        landedCallIdRef.current === lastCallIdRef.current
      const callId = ++lastCallIdRef.current

      return getRequestProfileTabCounters({
        id: requestId,
        outboundUuid,
        params: getRequestProfileTabCountersParams(
          isPartial ? sections : EMPTY_LIST
        ),
      })
        .then(response => {
          if (callId === lastCallIdRef.current) {
            landedCallIdRef.current = callId

            const counters = response?.data || EMPTY_OBJECT

            setLoaded(previousLoaded => ({
              counters: isPartial
                ? { ...previousLoaded.counters, ...counters }
                : counters,
              requestId,
            }))
          }

          return response
        })
        .catch(() => {
          if (!isPartial && callId === lastCallIdRef.current) {
            setLoaded({ counters: EMPTY_OBJECT, requestId })
          }

          return null
        })
    },
    [outboundUuid, requestId]
  )

  useEffect(() => {
    if (requestId != null) {
      handleLoadCounters()
    }
  }, [outboundUuid, requestId])

  const isCountersReady = requestId != null && loaded.requestId === requestId

  return {
    counters: isCountersReady ? loaded.counters : EMPTY_OBJECT,
    handleLoadCounters,
    isCountersReady,
  }
}

export default useRequestProfileTabsCounters
